// src/NavBar.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./NavBar.css";
import logo from "./logo.jpeg";
import {
  FaBars,
  FaTimes,
  FaUser,
  FaShoppingCart,
  FaChevronRight,
  FaChevronDown,
  FaSearch,
  FaFilePdf,
} from "react-icons/fa";
import { useCart } from "./context/CartContext";
import { db, auth } from "./firebase";
import { collection, collectionGroup, getDocs } from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { slugify } from "../src/slugify";
import CatalogGateModal from "./CatalogGateModal";


// Fixed, predictable Storage path generateCatalogPdf.js (Cloud Function)
// always publishes the latest catalog PDF to -- a direct link needs no
// Firestore read on page load, and no per-click generation wait.
const CATALOG_PDF_URL =
  "https://storage.googleapis.com/biba-website.firebasestorage.app/catalog/biba-electronics-product-catalog.pdf";

const CATEGORY_ACCENTS = [
  "#10b981",
  "#0ea5e9",
  "#8b5cf6",
  "#f59e0b",
  "#ef4444",
  "#14b8a6",
  "#6366f1",
  "#ec4899",
];

const accentForCategory = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CATEGORY_ACCENTS[Math.abs(hash) % CATEGORY_ACCENTS.length];
};




const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const NavBar = () => {
  const [activeCategory, setActiveCategory] = useState("");
  const [hoveredSubCategory, setHoveredSubCategory] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileProducts, setShowMobileProducts] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCatalogGate, setShowCatalogGate] = useState(false);

  // productData[categoryName] = {
  //   [subCategoryName]: { subtitle, icon, categoryId, subCategoryId, imageUrl }
  // }
  const [productData, setProductData] = useState({});

  // Real auth state instead of a hardcoded boolean
  const [authUser, setAuthUser] = useState(null);
  const isLoggedIn = !!authUser;
  const displayName =
    authUser?.displayName ||
    (authUser?.email ? authUser.email.split("@")[0] : "");

  const productsRef = useRef(null);
  const servicesRef = useRef(null);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems } = useCart();

  // Fetches product_categories → subCategories only (cheap, runs once on
  // mount, every page). Actual product rows are NOT fetched here — this
  // menu only needs to know category/subcategory names so it can route
  // to the grid page (ProductListing.jsx), which does the real product
  // fetch scoped to whichever subcategory the person clicked.
  const loadCategories = async () => {
    try {
      const [categorySnapshot, productSnapshot] = await Promise.all([
        getDocs(collection(db, "product_categories")),
        // Fetched once up front so every subcategory row can show how many
        // products it holds -- cheaper than a separate count query per
        // subcategory, and this catalog is small enough that pulling every
        // productData doc is fine (SearchResultsPage.jsx does the same).
        getDocs(collectionGroup(db, "productData")),
      ]);

      const productCountByKey = new Map();
      productSnapshot.forEach((d) => {
        const subRef = d.ref.parent.parent;
        const catRef = subRef?.parent?.parent;
        if (!subRef || !catRef) return;
        const key = `${catRef.id}/${subRef.id}`;
        productCountByKey.set(key, (productCountByKey.get(key) || 0) + 1);
      });

      const categoryEntries = await Promise.all(
        categorySnapshot.docs.map(async (categoryDoc) => {
          const categoryData = categoryDoc.data();
          const categoryName = categoryData.categoryName;

          if (!categoryName) return null;

          const subSnapshot = await getDocs(
            collection(
              db,
              "product_categories",
              categoryDoc.id,
              "subCategories"
            )
          );

          const subEntries = subSnapshot.docs.map((subDoc) => {
            const subData = subDoc.data();
            return {
              subCategoryName: subData.subCategoryName || subDoc.id,
              displayOrder:
                typeof subData.displayOrder === "number"
                  ? subData.displayOrder
                  : Number.MAX_SAFE_INTEGER,
              meta: {
                subtitle: subData.subtitle || "",
                icon: subData.icon || "📦",
                categoryId: categoryDoc.id,
                subCategoryId: subDoc.id,
                imageUrl: subData.subCategoryImageUrl || null,
                productCount: productCountByKey.get(`${categoryDoc.id}/${subDoc.id}`) || 0,
                // Pinned by functions/catalogSync.js for a sub-category with
                // no stock SKUs (Segmented Display): its row leads to a custom
                // landing page, so "0 items" would read as a dead end.
                customOnly: subData.customOnly === true,
              },
            };
          });
          subEntries.sort((a, b) => a.displayOrder - b.displayOrder);

          const subcats = {};
          subEntries.forEach((entry) => {
            subcats[entry.subCategoryName] = entry.meta;
          });

          return {
            categoryName,
            subcats,
            displayOrder:
              typeof categoryData.displayOrder === "number"
                ? categoryData.displayOrder
                : Number.MAX_SAFE_INTEGER,
          };
        })
      );

      const validEntries = categoryEntries.filter(Boolean);
      validEntries.sort((a, b) => a.displayOrder - b.displayOrder);

      const categories = {};
      validEntries.forEach((entry) => {
        categories[entry.categoryName] = entry.subcats;
      });

      setProductData(categories);
    } catch (error) {
      console.error(error);
    }
  };

  // Track real Firebase auth state so the navbar always reflects
  // who's actually signed in (fixes the previous hardcoded isLoggedIn = true)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
    });
    return unsub;
  }, []);

  // Publishes the *actually correct* viewport width as a CSS custom
  // property, because window.innerWidth (and therefore every
  // position:fixed element sized off plain `width:100%`/`vw`) can get
  // stuck reporting a stale, too-wide value on some mobile browsers --
  // confirmed on a real phone and reproduced here: window.innerWidth
  // sat at roughly double the true width for seconds at a time on first
  // load of some routes, only correcting itself after an interaction,
  // while window.visualViewport.width was correct from the very first
  // reading. The fixed navbar, its mobile drawer, and the product-
  // listing filter drawer/trigger all read --real-vw instead of relying
  // on native viewport units, so they can't inherit that staleness.
  useEffect(() => {
    const root = document.documentElement;
    const setRealVw = () => {
      const w = window.visualViewport?.width || window.innerWidth;
      root.style.setProperty("--real-vw", `${w}px`);
    };
    setRealVw();
    window.visualViewport?.addEventListener("resize", setRealVw);
    window.addEventListener("resize", setRealVw);
    window.addEventListener("orientationchange", setRealVw);
    return () => {
      window.visualViewport?.removeEventListener("resize", setRealVw);
      window.removeEventListener("resize", setRealVw);
      window.removeEventListener("orientationchange", setRealVw);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        productsRef.current &&
        !productsRef.current.contains(event.target) &&
        servicesRef.current &&
        !servicesRef.current.contains(event.target)
      ) {
        setMenuOpen(null);
      }

      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMenuOpen(null);
  }, [location.pathname]);

  // Locking scroll with plain `overflow:hidden` is the one thing NOT to do
  // for a full-screen `position:fixed` drawer on iOS Safari -- with
  // overflow:hidden on <body> while the page has any elastic/rubber-band
  // scroll state pending, Safari can paint fixed-position descendants at
  // the wrong offset (drawer/backdrop rendered squeezed off to one side,
  // reachable only at the very edge if at all -- the classic "menu is cut
  // off" iOS report). Pinning body itself with position:fixed and
  // restoring the saved scroll position on close is the standard, iOS-safe
  // way to lock background scroll.
  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY, 10) * -1);
      }
    }
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    loadCategories();
  }, []);

  // Now actually signs the user out of Firebase before routing away,
  // and sends them to /login instead of /
  const handleLogout = async () => {
    setShowUserMenu(false);
    try {
      await signOut(auth);
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Logout error:", err);
      navigate("/login", { replace: true });
    }
  };

  const handleHomeClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 300);
    }
    setMenuOpen(null);
    setIsMobileMenuOpen(false);
  };

  const closeAll = () => {
    setMenuOpen(null);
    setIsMobileMenuOpen(false);
  };

  const isMobile = () => window.innerWidth <= 768;

  const currentSubCategories = productData[activeCategory] || {};
  const subCategoryCount = Object.keys(currentSubCategories).length;

  useEffect(() => {
    const categories = Object.keys(productData);
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [productData, activeCategory]);

  // Hovering a category in the sidebar (desktop only) — swaps the preview
  // panel on the right immediately, the way most mega-menus behave, so
  // people don't have to click just to see what's inside a category.
  const handleCategoryHover = (category) => {
    if (!isMobile()) {
      setActiveCategory(category);
      setHoveredSubCategory(null);
    }
  };

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    if (isMobile()) setShowMobileProducts(true);
  };

  // Hovering a subcategory (desktop only) — purely visual highlight, no
  // fetch, no navigation. Kept lightweight since the real product fetch
  // now lives entirely in ProductListing.jsx.
  const handleSubCategoryEnter = (subCategoryName) => {
    if (!isMobile()) setHoveredSubCategory(subCategoryName);
  };

  // Clicking a subcategory row — navigates straight to the grid page for
  // that subcategory (ProductListing.jsx, mounted at
  // /category/:categorySlug/:subCategorySlug), using the SAME slugify()
  // function ProductListing.jsx uses to resolve the slug back to the
  // Firestore doc.
  // LANDING_PAGES_WIRED: two sub-categories have dedicated, prerendered
  // landing pages (src/pages/Display/HdmiDisplayPage.jsx and
  // UartDisplayPage.jsx). Send menu clicks there instead of the generic
  // Firestore grid; each landing page links back to its full grid.
  const SUBCATEGORY_LANDING_PAGES = {
    "smart-uart-display": "/uart-display",
    "hdmi-displays": "/hdmi-display",
  };

  const handleSubCategoryClick = (subCategoryName) => {
    const categorySlug = slugify(activeCategory);
    const subCategorySlug = slugify(subCategoryName);
    const landing = SUBCATEGORY_LANDING_PAGES[subCategorySlug];
    navigate(landing || `/category/${categorySlug}/${subCategorySlug}`);
    closeAll();
  };

  // Global search -- looks across every category, not just the one being
  // browsed (see SearchResultsPage.jsx). An empty query still navigates,
  // landing on the search page's own input rather than doing nothing.
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setSearchQuery("");
    closeAll();
  };

  return (
    <>
      {/* Blurred backdrop behind the open dropdown — dims and blurs the
          page content below the navbar (the navbar itself stays sharp),
          same effect as Newhaven Display's mega-menu. Clicking it closes
          whichever dropdown is open. */}
      {menuOpen && !isMobile() && (
        <div className="nav-backdrop" onClick={closeAll} />
      )}

      <nav className="navbar">
        <div className="logo">
          <a href="/">
            <img src={logo} alt="BiBA Logo" width="102" height="68" decoding="async" />
          </a>
        </div>

        <ul className={`nav-links ${isMobileMenuOpen ? "open" : ""}`}>
          <li>
            <Link to="/" onClick={handleHomeClick}>
              Home
            </Link>
          </li>

          <li>
            <Link to="/about" onClick={closeAll}>
              About
            </Link>
          </li>

          <li
            className={`dropdown dropdown-mega ${menuOpen === "products" ? "open" : ""}`}
            ref={productsRef}
            onMouseEnter={() => !isMobile() && setMenuOpen("products")}
            onMouseLeave={() => {
              if (!isMobile()) {
                setMenuOpen(null);
                setHoveredSubCategory(null);
              }
            }}
          >
            <div className="dropdown-wrapper">
              <span
                className="dropdown-btn"
                onClick={() => {
                  if (isMobile()) {
                    setShowMobileProducts(false);
                    setMenuOpen(menuOpen === "products" ? null : "products");
                  }
                }}
              >
                Products
                <FaChevronDown
                  className={`dropdown-caret-icon ${menuOpen === "products" ? "open" : ""
                    }`}
                />
              </span>

              {menuOpen === "products" && (
                <div className={`dropdown-menu mega-menu ${isMobile() ? "mobile" : ""}`}>
                  {/* Mobile: back button to return from subcategory list to category list */}
                  {isMobile() && showMobileProducts && (
                    <button
                      type="button"
                      className="mega-back-btn"
                      onClick={() => setShowMobileProducts(false)}
                    >
                      <FaChevronRight style={{ transform: "rotate(180deg)" }} />
                      All categories
                    </button>
                  )}

                  <div className="mega-menu-inner">
                    {/* VERTICAL CATEGORY SIDEBAR */}
                    {(!isMobile() || !showMobileProducts) && (
                      <div className="mega-sidebar">
                        <div className="mega-sidebar-label"> Categories</div>
                        <div className="mega-sidebar-list">
                          {Object.keys(productData).map((category) => (
                            <button
                              key={category}
                              type="button"
                              className={`mega-sidebar-item ${activeCategory === category ? "active" : ""
                                }`}
                              onMouseEnter={() => handleCategoryHover(category)}
                              onClick={() => handleCategoryClick(category)}
                            >
                              <span className="mega-sidebar-item-left">
                                <span
                                  className="mega-sidebar-marker"
                                  style={{ color: accentForCategory(category) }}
                                />
                                <span>{category}</span>
                              </span>
                              <FaChevronRight className="mega-sidebar-arrow" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* SUBCATEGORY LIST PANEL (MIDDLE) */}
                    {(!isMobile() || showMobileProducts) && (
                      <div className="mega-panel">
                        <div className="mega-panel-header">
                          <h4>{activeCategory}</h4>
                          <span className="mega-panel-count">
                            {subCategoryCount} {subCategoryCount === 1 ? "item" : "items"}
                          </span>
                        </div>

                        <div className="mega-panel-grid">
                          {Object.entries(currentSubCategories).map(
                            ([subCategoryName, meta], idx) => (
                              <div
                                key={subCategoryName}
                                className={`mega-panel-item ${hoveredSubCategory === subCategoryName
                                    ? "active"
                                    : ""
                                  }`}
                                onMouseEnter={() =>
                                  handleSubCategoryEnter(subCategoryName)
                                }
                                onMouseLeave={() =>
                                  !isMobile() && setHoveredSubCategory(null)
                                }
                                onClick={() =>
                                  handleSubCategoryClick(subCategoryName)
                                }
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleSubCategoryClick(subCategoryName);
                                  }
                                }}
                              >
                                <span className="mega-panel-icon">{meta.icon}</span>
                                <div className="mega-panel-text">
                                  <h5>{subCategoryName}</h5>
                                  {meta.subtitle && <p>{meta.subtitle}</p>}
                                </div>
                                <span className="mega-panel-tag">
                                  {meta.customOnly
                                    ? "Custom-built"
                                    : `${meta.productCount} ${meta.productCount === 1 ? "item" : "items"}`}
                                </span>
                              </div>
                            )
                          )}

                          {subCategoryCount === 0 && (
                            <div className="mega-panel-empty">
                              No products listed under this category yet.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* IMAGE PREVIEW PANEL (RIGHT) */}
                    {(!isMobile() || showMobileProducts) && (
                      <div className="mega-image-panel">
                        {hoveredSubCategory && currentSubCategories[hoveredSubCategory]?.imageUrl && (
                          <div className="mega-image-container">
                            <img
                              src={currentSubCategories[hoveredSubCategory].imageUrl}
                              alt={hoveredSubCategory}
                              className="mega-image-large"
                            />
                            <div className="mega-image-info">
                              <h5>{hoveredSubCategory}</h5>
                              {currentSubCategories[hoveredSubCategory]?.subtitle && (
                                <p>{currentSubCategories[hoveredSubCategory].subtitle}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </li>

          <li
            className={`dropdown ${menuOpen === "services" ? "open" : ""}`}
            ref={servicesRef}
            onMouseEnter={() => !isMobile() && setMenuOpen("services")}
            onMouseLeave={() => !isMobile() && setMenuOpen(null)}
          >
            <div className="dropdown-wrapper">
              <span
                className="dropdown-btn"
                onClick={() => {
                  if (isMobile()) setMenuOpen(menuOpen === "services" ? null : "services");
                }}
              >
                Services
                <FaChevronDown
                  className={`dropdown-caret-icon ${menuOpen === "services" ? "open" : ""
                    }`}
                />
              </span>

              {menuOpen === "services" && (
                <div className={`dropdown-menu services-menu ${isMobile() ? "mobile" : ""}`}>
                  <div className="services-list">
                    <Link to="/ems" className="service-btn" onClick={closeAll}>
                      ⚙️ Electronic Manufacturing
                    </Link>
                    <Link to="/embedded" className="service-btn" onClick={closeAll}>
                      💡 Embedded Design
                    </Link>
                    <Link to="/dsync-studio-features" className="service-btn" onClick={closeAll}>
                      🖥️ Dsync Studio
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </li>

          <li>
            <Link to="/careers" onClick={closeAll}>
              Careers
            </Link>
          </li>

          <li>
            <Link to="/contact" onClick={closeAll}>
              Contact
            </Link>
          </li>
        </ul>

        <div className="navbar-actions">

          <button
            type="button"
            onClick={() => setShowCatalogGate(true)}
            className="catalog-download-btn"
            title="Download our product catalog (PDF)"
          >
            <FaFilePdf />
            <span className="catalog-btn-label">Download Catalog</span>
          </button>

          <form className="navbar-search-form" onSubmit={handleSearchSubmit} role="search">
            <input
              type="search"
              className="navbar-search-input"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search products"
            />
            <button type="submit" className="navbar-action-btn navbar-search-btn" aria-label="Search">
              <FaSearch />
            </button>
          </form>

          <div className="user-menu-container" ref={userMenuRef}>
            <button
              className={`navbar-action-btn ${isLoggedIn ? "user-avatar-btn" : ""}`}
              onClick={() => {
                if (isLoggedIn) {
                  setShowUserMenu(!showUserMenu);
                } else {
                  navigate("/login");
                }
              }}
              aria-label="Profile"
            >
              {isLoggedIn && displayName ? (
                <span className="user-avatar-initials">{getInitials(displayName)}</span>
              ) : (
                <FaUser />
              )}
            </button>

            {showUserMenu && isLoggedIn && (
              <div className="user-dropdown">
                <button
                  className="user-dropdown-item"
                  onClick={() => {
                    navigate("/account");
                    setShowUserMenu(false);
                  }}
                >
                  My Account
                </button>

                <button
                  className="user-dropdown-item"
                  onClick={() => {
                    navigate("/my-orders");
                    setShowUserMenu(false);
                  }}
                >
                  My Orders
                </button>

                <button className="user-dropdown-item logout" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>

          <button className="navbar-action-btn cart-btn" onClick={() => navigate("/cart")}>
            <FaShoppingCart />
            <span className="cart-count">
              {cartItems.reduce((total, item) => total + (item.quantity || 1), 0)}
            </span>
          </button>


        </div>

        <div className="hamburger" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </div>
      </nav>

      {showCatalogGate && (
        <CatalogGateModal pdfUrl={CATALOG_PDF_URL} onClose={() => setShowCatalogGate(false)} />
      )}
    </>
  );
};

export default NavBar;